/* Hybrid content store: Firestore when configured, localStorage fallback.
   Also handles local admin master password for bootstrap. */

import { DEFAULT_CONTENT, SECTION_KEYS } from "./defaults";
import { firebaseReady, fbReadDoc, fbWriteDoc } from "./firebase";

const LS_KEY = "ka_site_content";
const LS_ADMIN_PWD = "ka_admin_pwd_hash_v2";
const LS_ADMIN_SESSION = "ka_admin_session";

/* ---------- password helpers (local fallback admin) ---------- */
async function sha256(text) {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

export async function ensureDefaultAdminPwd() {
    // Clean up old (legacy) hash key if present
    try {
        localStorage.removeItem("ka_admin_pwd_hash");
    } catch {}
    if (!localStorage.getItem(LS_ADMIN_PWD)) {
        // Default password: Konul32
        const h = await sha256("Konul32");
        localStorage.setItem(LS_ADMIN_PWD, h);
    }
}

export async function verifyAdminPwd(pwd) {
    await ensureDefaultAdminPwd();
    const h = await sha256(pwd);
    return h === localStorage.getItem(LS_ADMIN_PWD);
}

export async function setAdminPwd(pwd) {
    const h = await sha256(pwd);
    localStorage.setItem(LS_ADMIN_PWD, h);
}

export function setAdminSession(on) {
    if (on) localStorage.setItem(LS_ADMIN_SESSION, "1");
    else localStorage.removeItem(LS_ADMIN_SESSION);
}

export function isAdminLoggedIn() {
    return localStorage.getItem(LS_ADMIN_SESSION) === "1";
}

/* ---------- content load/save ---------- */
function loadLocal() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Merge with defaults to ensure forward compatibility
        return { ...DEFAULT_CONTENT, ...parsed };
    } catch {
        return null;
    }
}

function saveLocal(content) {
    localStorage.setItem(LS_KEY, JSON.stringify(content));
}

export async function loadContent() {
    if (firebaseReady()) {
        try {
            const data = await fbReadDoc("ka_site/content");
            if (data) {
                const merged = { ...DEFAULT_CONTENT, ...data };
                saveLocal(merged); // cache locally
                return merged;
            }
        } catch (e) {
            console.warn("Firestore load failed, using local cache", e);
        }
    }
    return loadLocal() || DEFAULT_CONTENT;
}

export async function saveContent(content) {
    saveLocal(content);
    if (firebaseReady()) {
        try {
            await fbWriteDoc("ka_site/content", content);
        } catch (e) {
            console.warn("Firestore save failed, saved locally only", e);
            throw e;
        }
    }
}

export async function updateSection(key, value) {
    const current = await loadContent();
    const next = { ...current, [key]: value };
    await saveContent(next);
    return next;
}

export { SECTION_KEYS, DEFAULT_CONTENT };
